<script setup>
import { watch, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useMainStore } from '~/stores/main-store.js'
import { useSearchDialogStore } from '~/stores/search-dialog-store.js'
import { nextTick, useTemplateRef } from 'vue'

const searchStore = useSearchDialogStore()
const searchInput = ref(null)
const searchResults = ref(null)
const searchInputElement = useTemplateRef('searchInputElement')
let searchTimeout = null
const { locale } = useI18n()
const $route = useRoute()
const mainStore = useMainStore()

// Watch for dialog visibility changes to auto-focus the input
watch(() => searchStore.isVisible, (isVisible) => {
  if (isVisible) {
    nextTick(() => {
      searchInputElement.value?.focus()
    })
  }
})

const showDialog = () => {
  searchStore.showDialog()
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('touchstart', handleClickOutside, { passive: false })
  document.addEventListener('keydown', handleKeyNavigation)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('touchstart', handleClickOutside, { passive: false })
  document.removeEventListener('keydown', handleKeyNavigation)
  clearTimeout(searchTimeout)
})

function handleKeyNavigation(event) {
  if (!searchStore.showSearchResults || searchStore.allFlatResults.length === 0) return

  switch(event.key) {
    case 'ArrowDown':
      event.preventDefault()
      searchStore.activeResultIndex = Math.min(searchStore.activeResultIndex + 1, searchStore.allFlatResults.length - 1)
      scrollToActiveResult()
      break
    case 'ArrowUp':
      event.preventDefault()
      searchStore.activeResultIndex = Math.max(searchStore.activeResultIndex - 1, -1)
      scrollToActiveResult()
      break
    case 'Enter':
      event.preventDefault()
      if (searchStore.activeResultIndex >= 0) {
        const selectedItem = searchStore.allFlatResults[searchStore.activeResultIndex]
        navigateToResult(selectedItem, selectedItem.type, event)
      }
      break
    case 'Escape':
      event.preventDefault()
      searchStore.hideDialog()
      break
  }
}

function scrollToActiveResult() {
  if (searchStore.activeResultIndex >= 0) {
    const activeElements = document.querySelectorAll('.search-result-item')
    if (activeElements[searchStore.activeResultIndex]) {
      activeElements[searchStore.activeResultIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }
}

watch(() => searchStore.searchTerm, (newValue) => {
  debouncedSearch()

  if (newValue && newValue.length >= 2) {
    searchStore.showSearchResults = true
  } else {
    searchStore.showSearchResults = false
  }
})

function debouncedSearch() {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    searchStore.performSearch()
  }, 300)
}

function navigateToResult(item, type, event) {
  // Prevent default behavior for touch events to avoid any conflicts
  if (event && event.type === 'touchstart') {
    event.preventDefault()
  }

  searchStore.saveToRecentSearches(searchStore.searchTerm)
  const originalTerm = searchStore.searchTerm
  searchStore.hideDialog()

  let route = ''

  switch (type) {
    case 'publication':
      // Use direct URL construction like in PublicationItem.vue
      const filteredDates = getFilteredDates(item)
      if (filteredDates && filteredDates.length === 1) {
        route = `/${locale.value}/${item.slug}-tickets/${filteredDates[0].id}`
      } else {
        route = `/${locale.value}/${item.slug}-tickets`
      }
      break
    case 'artist':
      route = {
        name: `artist-username___${locale.value}`,
        params: {
          username: item.username
        }
      }
      break
    case 'producer':
      route = {
        name: `producer-username___${locale.value}`,
        params: {
          username: item.username
        }
      }
      break
    case 'location':
      route = {
        name: `locations-slug___${locale.value}`,
        params: {
          slug: item.slug
        }
      }
      break
  }

  if (route) {
    try {
      console.info('Búsqueda realizada:', {
        term: originalTerm,
        resultSelected: {
          type,
          id: item.id,
          name: item.title || item.name
        }
      })
    } catch (e) {}

    // Use nextTick to ensure the dialog is fully hidden before navigation
    nextTick(() => {
      // Handle both string URLs and route objects
      if (typeof route === 'string') {
        navigateTo(route)
      } else {
        navigateTo(route)
      }
    })
  }
}

function highlightMatch(text, term) {
  if (!term || !text) return text

  const regex = new RegExp(`(${term.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')})`, 'gi')
  return text.replace(regex, '<mark class="bg-primary-100 dark:bg-primary-900 px-0.5 rounded">$1</mark>')
}

function handleClickOutside(event) {
  // Only hide results if click is outside both search input and search results container
  if (searchInput.value && !searchInput.value.contains(event.target) &&
      searchResults.value && !searchResults.value.contains(event.target) &&
      event?.target?.tagName?.toLowerCase() !== 'i') {
    searchStore.showSearchResults = false
  }
}

const getFilteredDates = (publication) => {
  return publication?.dates
}

const goToPublicationDetail = (publication) => {
  if (getFilteredDates(publication).length === 1) {
    return `/${locale.value}/${publication?.slug}-tickets/${getFilteredDates(publication)[0].id}`
  } else {
    return `/${locale.value}/${publication?.slug}-tickets`
  }
}

defineExpose({
  showDialog
})
</script>

<template>
  <div v-if="searchStore.isVisible" class="search-dialog-overlay" @click="searchStore.hideDialog"></div>
  <div
    v-if="searchStore.isVisible"
    class="search-dialog fixed inset-0 z-50 bg-white dark:bg-gray-900 overflow-hidden"
  >
    <div class="search-dialog-header flex justify-between items-center p-4 border-b border-gray-100 dark:border-gray-700">
      <h2 class="text-lg font-medium text-gray-800 dark:text-white">Buscar</h2>
      <button
        @click="searchStore.hideDialog"
        class="rounded-full p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <div class="search-dialog-content p-4 md:p-6 h-[calc(100vh-60px)]">
      <div class="flex flex-col relative gap-3 max-w-3xl mx-auto">
        <div ref="searchInput" class="w-full relative">
          <svg
            class="absolute z-20 left-4 top-1/2 transform -translate-y-1/2 w-[15px] fill-gray-400"
            viewBox="0 0 15 15"
          >
            <path d="M5.57143 0C7.04906 0 8.46618 0.586988 9.51102 1.63183C10.5559 2.67668 11.1429 4.09379 11.1429 5.57143C11.1429 6.95143 10.6371 8.22 9.80571 9.19714L10.0371 9.42857H10.7143L15 13.7143L13.7143 15L9.42857 10.7143V10.0371L9.19714 9.80571C8.22 10.6371 6.95143 11.1429 5.57143 11.1429C4.09379 11.1429 2.67668 10.5559 1.63183 9.51102C0.586988 8.46618 0 7.04906 0 5.57143C0 4.09379 0.586988 2.67668 1.63183 1.63183C2.67668 0.586988 4.09379 0 5.57143 0ZM5.57143 1.71429C3.42857 1.71429 1.71429 3.42857 1.71429 5.57143C1.71429 7.71429 3.42857 9.42857 5.57143 9.42857C7.71429 9.42857 9.42857 7.71429 9.42857 5.57143C9.42857 3.42857 7.71429 1.71429 5.57143 1.71429Z" />
          </svg>
          <input
            ref="searchInputElement"
            v-model="searchStore.searchTerm"
            placeholder="Buscar evento o artista"
            class="pl-12 px-5 py-3 rounded-full border outline-0 w-full sm:mr-2 transition-all duration-300 border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-lg"
            style="font-size: 16px; -webkit-appearance: none; -webkit-tap-highlight-color: transparent;"
            autocapitalize="off"
            autocomplete="off"
            autocorrect="off"
          />

          <!-- Botón para limpiar la búsqueda -->
          <button
            v-if="searchStore.searchTerm.length > 0"
            @click="searchStore.resetSearch"
            class="absolute right-3 top-3 rounded-full w-7 h-7 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-600 bg-gray-100 text-gray-500 dark:bg-gray-600 dark:text-gray-300"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div
          ref="searchResults"
          v-if="searchStore.showSearchResults && searchStore.searchTerm.length > 0"
          class="search-results w-full h-[calc(100vh-180px)] overflow-y-auto bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 transition-all duration-300 ease-in-out mt-4"
        >
          <div v-if="searchStore.recentSearches.length > 0 && searchStore.searchTerm.length < 2" class="p-4 border-b border-gray-100 dark:border-gray-700">
            <div class="flex justify-between items-center mb-2">
              <h3 class="text-sm font-medium text-gray-500 dark:text-gray-400">Búsquedas recientes</h3>
              <button @click="searchStore.clearRecentSearches" class="text-xs text-primary-500 hover:text-primary-700">Limpiar</button>
            </div>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="(search, index) in searchStore.recentSearches"
                :key="`recent-${index}`"
                @click="searchStore.selectRecentSearch(search)"
                class="px-3 py-1 text-sm bg-gray-50 dark:bg-gray-700 rounded-full hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                {{ search }}
              </button>
            </div>
          </div>

          <div v-if="searchStore.isSearching" class="p-4 text-center text-gray-500 animate-pulse">
            <svg class="w-6 h-6 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Buscando...
          </div>

          <div v-else-if="!searchStore.hasResults() && searchStore.searchTerm.length >= 2" class="p-6 text-center">
            <svg class="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <p class="text-lg font-medium text-gray-500 mb-1">No se encontraron resultados</p>
            <p class="text-sm text-gray-400">Intenta con otras palabras o términos más generales</p>
          </div>

          <div v-else-if="searchStore.hasResults()" class="py-2">
            <div class="px-6 py-3 flex items-center text-base text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
              <span>{{ searchStore.totalResults() }} resultados para "{{ searchStore.searchTerm }}"</span>
              <div class="ml-auto text-sm flex items-center gap-2 text-primary-500">
                <kbd class="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded">↑</kbd>
                <kbd class="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded">↓</kbd>
                <span class="sm:hidden">para navegar</span>
                <kbd class="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded">Enter</kbd>
                <span class="sm:hidden">para seleccionar</span>
              </div>
            </div>
            <div v-if="searchStore.searchResults.publications && searchStore.searchResults.publications.length > 0" class="mb-2">
              <div class="px-6 py-3 bg-gray-50 dark:bg-gray-700 font-medium flex items-center justify-between">
                <span class="text-gray-500 dark:text-gray-400 text-base">Eventos</span>
                <span class="text-sm text-gray-500 dark:text-gray-400">{{ searchStore.searchResults.publications.length }} resultados</span>
              </div>
              <div
                v-for="(publication, index) in searchStore.searchResults.publications"
                :key="`pub-${publication.id}`"
                @click="(event) => navigateToResult(publication, 'publication', event)"
                @touchstart="(event) => navigateToResult(publication, 'publication', event)"
                @mouseover="searchStore.activeResultIndex = searchStore.allFlatResults.findIndex(r => r.id === publication.id && r.type === 'publication')"
                class="search-result-item px-6 py-4 flex items-center hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                :class="{'bg-gray-50 dark:bg-gray-700': searchStore.activeResultIndex === searchStore.allFlatResults.findIndex(r => r.id === publication.id && r.type === 'publication')}"
              >
                <div class="w-12 h-12 rounded overflow-hidden mr-3 flex-shrink-0">
                  <img v-if="publication.cover_image_url" :src="publication.cover_image_url" class="w-full h-full object-cover" :alt="publication.title">
                  <div v-else class="w-full h-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                    <svg class="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M4 4h16v16H4V4zm2 2v12h12V6H6z"></path>
                    </svg>
                  </div>
                </div>
                <div>
                  <p class="font-medium" v-html="highlightMatch(publication.title, searchStore.searchTerm)"></p>
                  <p class="text-sm text-gray-500" v-html="highlightMatch(publication.sub_title || publication.start_date, searchStore.searchTerm)"></p>
                </div>
              </div>
            </div>
            <div v-if="searchStore.searchResults.artists && searchStore.searchResults.artists.length > 0" class="mb-2">
              <div class="px-6 py-3 bg-gray-50 dark:bg-gray-700 font-medium flex items-center justify-between">
                <span class="text-gray-500 dark:text-gray-400 text-base">Perfiles</span>
                <span class="text-sm text-gray-500 dark:text-gray-400">{{ searchStore.searchResults.artists.length }} resultados</span>
              </div>
              <div
                v-for="(item, index) in searchStore.searchResults.artists"
                :key="`art-${item.id}`"
                @click="(event) => navigateToResult(item, 'artist', event)"
                @touchstart="(event) => navigateToResult(item, 'artist', event)"
                @mouseover="searchStore.activeResultIndex = searchStore.allFlatResults.findIndex(r => r.id === item.id && r.type === 'artist')"
                class="search-result-item px-6 py-4 flex items-center hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                :class="{'bg-gray-50 dark:bg-gray-700': searchStore.activeResultIndex === searchStore.allFlatResults.findIndex(r => r.id === item.id && r.type === 'artist')}"
              >
                <div class="w-12 h-12 rounded-full overflow-hidden mr-3 flex-shrink-0">
                  <img v-if="item.photo_url" :src="item.photo_url" class="w-full h-full object-cover" :alt="item.name">
                  <div v-else class="w-full h-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center rounded-full">
                    <span class="text-lg">{{ item.name.charAt(0) }}</span>
                  </div>
                </div>
                <div>
                  <p class="font-medium" v-html="highlightMatch(item.name, searchStore.searchTerm)"></p>
                  <p class="text-sm text-gray-500">@{{ item.username }}</p>
                </div>
              </div>
            </div>
            <div v-if="searchStore.searchResults.producers && searchStore.searchResults.producers.length > 0" class="mb-2">
              <div class="px-6 py-3 bg-gray-50 dark:bg-gray-700 font-medium flex items-center justify-between">
                <span class="text-gray-500 dark:text-gray-400 text-base">Productores</span>
                <span class="text-sm text-gray-500 dark:text-gray-400">{{ searchStore.searchResults.producers.length }} resultados</span>
              </div>
              <div
                v-for="(item, index) in searchStore.searchResults.producers"
                :key="`prod-${item.id}`"
                @click="(event) => navigateToResult(item, 'producer', event)"
                @touchstart="(event) => navigateToResult(item, 'producer', event)"
                @mouseover="searchStore.activeResultIndex = searchStore.allFlatResults.findIndex(r => r.id === item.id && r.type === 'producer')"
                class="search-result-item px-6 py-4 flex items-center hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                :class="{'bg-gray-50 dark:bg-gray-700': searchStore.activeResultIndex === searchStore.allFlatResults.findIndex(r => r.id === item.id && r.type === 'producer')}"
              >
                <div class="w-12 h-12 rounded overflow-hidden mr-3 flex-shrink-0">
                  <img v-if="item.photo_url" :src="item.photo_url" class="w-full h-full object-cover" :alt="item.name">
                  <div v-else class="w-full h-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                    <span class="text-lg">{{ item.name.charAt(0) }}</span>
                  </div>
                </div>
                <div>
                  <p class="font-medium" v-html="highlightMatch(item.name, searchStore.searchTerm)"></p>
                  <p class="text-sm text-gray-500">Productor</p>
                </div>
              </div>
            </div>
            <div v-if="searchStore.searchResults.locations && searchStore.searchResults.locations.length > 0" class="mb-2">
              <div class="px-6 py-3 bg-gray-50 dark:bg-gray-700 font-medium flex items-center justify-between">
                <span class="text-gray-500 dark:text-gray-400 text-base">Ubicaciones</span>
                <span class="text-sm text-gray-500 dark:text-gray-400">{{ searchStore.searchResults.locations.length }} resultados</span>
              </div>
              <div
                v-for="(item, index) in searchStore.searchResults.locations"
                :key="`loc-${item.id}`"
                @click="(event) => navigateToResult(item, 'location', event)"
                @touchstart="(event) => navigateToResult(item, 'location', event)"
                @mouseover="searchStore.activeResultIndex = searchStore.allFlatResults.findIndex(r => r.id === item.id && r.type === 'location')"
                class="search-result-item px-6 py-4 flex items-center hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                :class="{'bg-gray-50 dark:bg-gray-700': searchStore.activeResultIndex === searchStore.allFlatResults.findIndex(r => r.id === item.id && r.type === 'location')}"
              >
                <div class="w-12 h-12 rounded overflow-hidden mr-3 flex-shrink-0">
                  <img v-if="item.image_url" :src="item.image_url" class="w-full h-full object-cover" :alt="item.name">
                  <div v-else class="w-full h-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                    <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    </svg>
                  </div>
                </div>
                <div>
                  <p class="font-medium" v-html="highlightMatch(item.name, searchStore.searchTerm)"></p>
                  <p class="text-sm text-gray-500" v-html="highlightMatch(item.address || '', searchStore.searchTerm)"></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.search-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  z-index: 49;
  animation: fadeIn 0.2s ease-out;
}

.search-dialog {
  animation: scaleIn 0.2s ease-out;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.search-results {
  scrollbar-width: thin;
  scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
  animation: fadeInDown 0.2s ease-out;
  -webkit-overflow-scrolling: touch; /* Enable momentum scrolling on iOS */
  overscroll-behavior: contain; /* Prevent scroll chaining */
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.search-results::-webkit-scrollbar {
  width: 6px;
}

.search-results::-webkit-scrollbar-track {
  background: transparent;
}

.search-results::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 3px;
}

.dark .search-results {
  scrollbar-color: rgba(156, 163, 175, 0.3) transparent;
}

.dark .search-results::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.3);
}

.search-result-item {
  transition: background-color 0.15s ease;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  min-height: 60px; /* Ensure touch targets are large enough */
  user-select: none; /* Prevent text selection during touch */
}

mark {
  transition: background-color 0.3s ease;
}

button {
  opacity: 1;
  transition: opacity 0.2s ease-in-out, background-color 0.2s ease;
}

.search-dialog-content {
  max-height: calc(90vh - 60px);
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
  -webkit-overflow-scrolling: touch; /* Enable momentum scrolling on iOS */
  overscroll-behavior: contain; /* Prevent scroll chaining */
}

.search-dialog-content::-webkit-scrollbar {
  width: 6px;
}

.search-dialog-content::-webkit-scrollbar-track {
  background: transparent;
}

.search-dialog-content::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 3px;
}

.dark .search-dialog-content::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.3);
}
</style>
